import { makeGetRequest, makePostRequest, makeDeleteRequest } from './api'

/**
 * Wizard API utility functions
 * All functions handle authentication automatically via makeGetRequest/makePostRequest
 */

export const wizardAPI = {
  /**
   * Get wizard step data
   * @param {string} stepKey - Step identifier (e.g., "Welcome", "ONBOARDING_PRO_STATUT")
   * @returns {Promise<Object>} Wizard step data with categories and progress
   */
  getWizardStep: async (stepKey) => {
    try {
      const response = await makeGetRequest(`/api/app/wizard/?w=${stepKey}`, {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      if (response?.status === 403) {
        throw new Error('SUBSCRIPTION_REQUIRED')
      }
      
      if (response?.status === 404) {
        throw new Error('STEP_NOT_FOUND')
      }
      
      throw new Error('Failed to fetch wizard step')
    } catch (error) {
      console.error('Error fetching wizard step:', error)
      throw error
    }
  },

  /**
   * Update wizard progress for a step
   * @param {Object} params - { step: string, completed: boolean, skip: boolean }
   * @returns {Promise<Object>} Updated progress data
   */
  updateWizardProgress: async ({ step, completed, skip = false }) => {
    try {
      const response = await makePostRequest(
        '/api/app/user-wizard/',
        { step, completed, skip },
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      
      throw new Error('Failed to update wizard progress')
    } catch (error) {
      console.error('Error updating wizard progress:', error)
      throw error
    }
  },

  /**
   * Skip all wizard steps
   * @returns {Promise<Object>} Success response
   */
  skipAllWizardSteps: async () => {
    try {
      const response = await makePostRequest(
        '/api/app/user-wizard-all/',
        {},
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      
      throw new Error('Failed to skip all wizard steps')
    } catch (error) {
      console.error('Error skipping all wizard steps:', error)
      throw error
    }
  },

  /**
   * Save professional status (LAP-597)
   * @param {Object} params - { status: string }
   * @returns {Promise<Object>} Success response
   */
  saveProfessionalStatus: async ({ status }) => {
    try {
      const allowedStatuses = [
        'Actif salarié',
        'Actif en recherche d\'emploi',
        'Étudiant',
        'Retraité'
      ]
      
      if (!allowedStatuses.includes(status)) {
        throw new Error('Invalid professional status')
      }
      
      const response = await makePostRequest(
        '/api/app/professional-status/',
        { status },
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      
      throw new Error('Failed to save professional status')
    } catch (error) {
      console.error('Error saving professional status:', error)
      throw error
    }
  },

  /**
   * Create 25 objectives as Roadmap Ideas (LAP-687)
   * @param {Object} payload - { "0": "Goal 1", "1": "Goal 2", ... }
   * @returns {Promise<Object>} Created ideas data
   */
  createObjectives: async (payload) => {
    try {
      // Validate payload has at least one objective
      const objectives = Object.values(payload).filter(obj => obj && obj.trim())
      if (objectives.length === 0) {
        throw new Error('At least one objective is required')
      }
      
      const response = await makePostRequest(
        '/api/app/objective/',
        payload,
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      
      throw new Error('Failed to create objectives')
    } catch (error) {
      console.error('Error creating objectives:', error)
      throw error
    }
  },

  /**
   * Get user's roadmap ideas for the "select 5 objectives" screen.
   * Uses GET /api/app/objective/ (same as creation) so the list matches saved objectives.
   * @returns {Promise<Array>} Array of { id, title, ... } roadmap ideas
   */
  getObjectivesForSelection: async () => {
    try {
      const response = await makeGetRequest('/api/app/objective/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch user objectives')
    } catch (error) {
      console.error('Error fetching objectives for selection:', error)
      throw error
    }
  },

  /**
   * Update user's Top 5 selection (LAP-687)
   * Updates existing roadmap_idea records
   * @param {Object} params - { selected: string[] } - Array of idea IDs
   * @returns {Promise<Object>} Updated ideas data
   */
  updateUserObjectives: async ({ selected }) => {
    try {
      if (!Array.isArray(selected)) {
        throw new Error('Selected must be an array')
      }
      
      if (selected.length > 5) {
        throw new Error('Maximum 5 ideas can be selected')
      }
      
      const selectedIds = selected.map((s) => String(s)).filter(Boolean)
      if (selectedIds.length > 5) {
        throw new Error('Maximum 5 ideas can be selected')
      }
      if (selectedIds.length === 0) {
        throw new Error('Sélectionnez au moins un objectif')
      }

      const response = await makePostRequest(
        '/api/app/user-mesobjectiv/',
        { selected: selectedIds },
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      const err = new Error(response?.data?.error || 'Failed to update user objectives')
      err.response = { data: response?.data }
      throw err
    } catch (error) {
      console.error('Error updating user objectives:', error)
      throw error
    }
  },

  /**
   * Get financial profile summary (Spec 4.4)
   * @returns {Promise<Object>} Financial profile summary data
   */
  getFinancialProfileSummary: async () => {
    try {
      const response = await makeGetRequest('/api/app/wizard/financial-profile-summary/', {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      throw new Error('Failed to fetch financial profile summary')
    } catch (error) {
      console.error('Error fetching financial profile summary:', error)
      throw error
    }
  },

  /**
   * Get eco-footprint summary (Spec 4.6)
   * @returns {Promise<Object>} Eco-footprint summary data
   */
  getEcoFootprintSummary: async () => {
    try {
      const response = await makeGetRequest('/api/app/wizard/eco-footprint-summary/', {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      throw new Error('Failed to fetch eco-footprint summary')
    } catch (error) {
      console.error('Error fetching eco-footprint summary:', error)
      throw error
    }
  },

  /**
   * Get time management profile summary (Spec 4.7)
   * @returns {Promise<Object>} Time management profile summary data
   */
  getTimeManagementProfileSummary: async () => {
    try {
      const response = await makeGetRequest('/api/app/wizard/time-management-summary/', {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      throw new Error('Failed to fetch time management profile summary')
    } catch (error) {
      console.error('Error fetching time management profile summary:', error)
      throw error
    }
  },

  /**
   * Get professional experience summary (Spec 4.8)
   * @returns {Promise<Object>} Professional experience summary data
   */
  getProfessionalExperienceSummary: async () => {
    try {
      const response = await makeGetRequest('/api/app/wizard/professional-experience-summary/', {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      throw new Error('Failed to fetch professional experience summary')
    } catch (error) {
      console.error('Error fetching professional experience summary:', error)
      throw error
    }
  },

  /**
   * Get time breakdown for wizard (Spec 4.12)
   * @returns {Promise<Array>} Array of domains with current and desired hours
   */
  getTimeBreakdown: async () => {
    try {
      const response = await makeGetRequest('/api/app/wizard/time-breakdown/', {}, true)
      
      if (response?.status === 200) {
        return response.data
      }
      
      throw new Error('Failed to fetch time breakdown')
    } catch (error) {
      console.error('Error fetching time breakdown:', error)
      throw error
    }
  },

  /**
   * Get Mes objectifs metadata (domains, frequency, predefined_objectives) for evaluation objective selection
   * @returns {Promise<Object>} { domains, frequency, predefined_objectives }
   */
  getMesObjectifsMetadata: async () => {
    try {
      const response = await makeGetRequest('/api/general/mesobjectifs-metadata/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch metadata')
    } catch (error) {
      console.error('Error fetching mesobjectifs metadata:', error)
      throw error
    }
  },

  /**
   * Get user objectives (optionally by domain id) for listing on evaluation step
   * @param {number} [domainId] - optional domain id to filter
   * @returns {Promise<Object>} { results: [...], count }
   */
  getUserObjectives: async (domainId) => {
    try {
      const url = domainId ? `/api/general/mesobjectifs/?domain=${domainId}` : '/api/general/mesobjectifs/'
      const response = await makeGetRequest(url, {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch user objectives')
    } catch (error) {
      console.error('Error fetching user objectives:', error)
      throw error
    }
  },

  /**
   * Create user objective (4.11.3.1.3 Set goal)
   * @param {Object} payload - { domain, objectives_name, objectifs, unit_of_measurement, periodicity, date_objectives }
   * @returns {Promise<Object>} Created objective
   */
  createUserObjective: async (payload) => {
    try {
      const response = await makePostRequest('/api/general/mesobjectifs/', payload, {}, true)
      if (response?.status === 200 || response?.status === 201) return response.data
      throw new Error('Failed to create objective')
    } catch (error) {
      console.error('Error creating objective:', error)
      throw error
    }
  },

  /**
   * Create predefined objective for a domain (4.11.3.1.5 Add manual)
   * @param {Object} params - { domain: string, objective: string, unit: string }
   * @returns {Promise<{ id, objectives_name, value_format }>}
   */
  addPreDefinObjForDomain: async ({ domain, objective, unit = '%' }) => {
    try {
      const response = await makePostRequest(
        '/api/general/add-predefinobj-for-domain/',
        { domain, objective, unit },
        {},
        true
      )
      if (response?.status === 200) return response.data
      throw new Error(response?.data?.error || 'Failed to create predefined objective')
    } catch (error) {
      console.error('Error adding predefined objective:', error)
      throw error
    }
  },

  /**
   * Delete (cancel) a user objective
   * @param {number} id - user objective id
   * @returns {Promise<void>}
   */
  deleteUserObjective: async (id) => {
    try {
      const response = await makeDeleteRequest(`/api/general/mesobjectifs/${id}/`, {}, true)
      if (response?.status === 200 || response?.status === 204) return
      throw new Error('Failed to delete objective')
    } catch (error) {
      console.error('Error deleting objective:', error)
      throw error
    }
  },

  /**
   * Recalculate user priorities from domain evaluations (4.11.4)
   * @returns {Promise<Object>}
   */
  recalculatePriorities: async () => {
    try {
      const response = await makePostRequest(
        '/api/general/recalculate-priorities/',
        {},
        {},
        true
      )
      if (response?.status === 200) return response.data
      throw new Error('Failed to recalculate priorities')
    } catch (error) {
      console.error('Error recalculating priorities:', error)
      throw error
    }
  },

  /**
   * Get current user's domain evaluations (for pre-filling evaluation steps)
   * @returns {Promise<Array<{ domain, importance_score, emotion_score }>>}
   */
  getUserEvaluations: async () => {
    try {
      const response = await makeGetRequest('/api/general/user-evaluations/', {}, true)
      if (response?.status === 200 && Array.isArray(response.data)) return response.data
      return []
    } catch (error) {
      console.error('Error fetching user evaluations:', error)
      return []
    }
  },

  /**
   * Save domain evaluation (GE015 4.11.3 - importance, emotion, optional objectives)
   * @param {Object} params - { domain: string, importance_score: number, emotion_score: number, objectiveList?: array }
   * @returns {Promise<Object>} Success response
   */
  saveDomainEvaluation: async ({ domain, importance_score, emotion_score, objectiveList = [] }) => {
    try {
      const response = await makePostRequest(
        '/api/general/add-user-evaluation/',
        { domain, importance_score, emotion_score, objectiveList },
        {},
        true
      )
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      const err = new Error(response?.data?.error || 'Failed to save domain evaluation')
      err.response = { data: response?.data }
      throw err
    } catch (error) {
      console.error('Error saving domain evaluation:', error)
      throw error
    }
  },

  /**
   * Get user health data (4.11.5.2)
   * @returns {Promise<Object>}
   */
  getHealth: async () => {
    try {
      const response = await makeGetRequest('/api/health/health/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch health data')
    } catch (error) {
      console.error('Error fetching health:', error)
      throw error
    }
  },

  /**
   * Save user health (4.11.5.2) – preventive_follow_up, medical_consultations, physical_wellbeing_score
   * @param {Object} data
   * @returns {Promise<void>}
   */
  saveUserHealth: async (data) => {
    try {
      const response = await makePostRequest('/api/health/health/', data, {}, true)
      if (response?.status === 200 || response?.status === 201) return
      throw new Error('Failed to save health data')
    } catch (error) {
      console.error('Error saving health:', error)
      throw error
    }
  },

  /**
   * Get list of sports (4.11.5.1 – wizard Perso Sport)
   * @returns {Promise<Array>}
   */
  getSports: async () => {
    try {
      const response = await makeGetRequest('/api/health/sport/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch sports')
    } catch (error) {
      console.error('Error fetching sports:', error)
      throw error
    }
  },

  /**
   * Add a custom sport to the list (creates in Sport table if not exists)
   * @param {string} name - sport name
   * @returns {Promise<{ id, sport_name, created }>}
   */
  addSport: async (name) => {
    try {
      const response = await makePostRequest(
        '/api/health/sport/add/',
        { name: (name || '').trim() },
        {},
        true
      )
      if (response?.status === 200 || response?.status === 201) return response.data
      throw new Error(response?.data?.error || 'Failed to add sport')
    } catch (error) {
      console.error('Error adding sport:', error)
      throw error
    }
  },

  /**
   * Save user sports (sport name + sessions per month) and create Sport user objective (4.11.5.1.3)
   * @param {Array<{ name: string, value: number }>} list
   * @returns {Promise<void>}
   */
  saveUserSports: async (list) => {
    try {
      const response = await makePostRequest('/api/health/sport/', list, {}, true)
      if (response?.status === 200 || response?.status === 201) return
      throw new Error('Failed to save sports')
    } catch (error) {
      console.error('Error saving sports:', error)
      throw error
    }
  },

  /**
   * Get leisure activities list (4.11.5.3)
   * @returns {Promise<Array>}
   */
  getLeisure: async () => {
    try {
      const response = await makeGetRequest('/api/general/leisure/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch leisure activities')
    } catch (error) {
      console.error('Error fetching leisure:', error)
      throw error
    }
  },

  /**
   * Save user leisure (4.11.5.3) – list of { name, value } (desired_time_percent)
   * @param {Array<{ name: string, value: number }>} list
   * @returns {Promise<void>}
   */
  saveUserLeisure: async (list) => {
    try {
      const response = await makePostRequest('/api/general/leisure/', list, {}, true)
      if (response?.status === 200 || response?.status === 201) return
      throw new Error('Failed to save leisure data')
    } catch (error) {
      console.error('Error saving leisure:', error)
      throw error
    }
  },

  /**
   * Get text combination for user portrait (4.13.1.2) – domains, text_combination, user_timebreack_text, user_finance_profile, user_time_profile
   * @returns {Promise<Object>}
   */
  getTextCombination: async () => {
    try {
      const response = await makeGetRequest('/api/general/text-combination/', {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch text combination')
    } catch (error) {
      console.error('Error fetching text combination:', error)
      throw error
    }
  },

  /**
   * LAP-752 — generate (or refresh) the user's ONBOARDING Life Resume snapshot
   * so the WheelOfLife radar at the end of the wizard reads the same scores
   * that Life Resume shows. Idempotent server-side. Returns the snapshot
   * payload on success or null on failure (caller falls back gracefully).
   */
  generateOnboardingSnapshot: async () => {
    try {
      const response = await makePostRequest(
        '/api/general/life-resume/generate/',
        {},
        {},
        true,
      )
      if (response?.status >= 200 && response?.status < 300) return response.data
      return null
    } catch (error) {
      console.error('Error generating onboarding snapshot:', error)
      return null
    }
  },

  /**
   * LAP-752 — fetch the Life Resume radar data (same source the dedicated
   * Life Resume page uses). Optional `params` include `type=ONBOARDING`,
   * `limit`, `period_type`, `year`, `month`. Returns `{ domains, series }`.
   */
  getLifeResumeRadar: async (params = {}) => {
    try {
      const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
      const url = qs
        ? `/api/general/life-resume/radar/?${qs}`
        : '/api/general/life-resume/radar/'
      const response = await makeGetRequest(url, {}, true)
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch life-resume radar data')
    } catch (error) {
      console.error('Error fetching life-resume radar data:', error)
      throw error
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // LAP-752 — Ecological dashboard endpoints
  // ─────────────────────────────────────────────────────────────────

  /**
   * Activate an ecological recommendation. Backend creates the "-X% sur
   * l'année" yearly objective from the Claude-suggested reduction_percentage
   * and links it to the DisplayedAdvice row (idempotent: 409 if already
   * activated). LAP-752 Phase 3.
   *
   * @param {number|string} adviceId - DisplayedAdvice id
   * @returns {Promise<Object>} { objective_id, objective_name, reduction_percentage, date_echeance }
   * @throws Error with `.status` 404 / 400 / 409 / 500
   */
  activateEcoRecommendation: async (adviceId) => {
    try {
      const response = await makePostRequest(
        `/api/Dashboard/activate-recommendation/${adviceId}/`,
        {},
        {},
        true
      )
      if (response?.status === 201 || response?.status === 200) {
        return response.data
      }
      const err = new Error(response?.data?.detail || 'Failed to activate recommendation')
      err.status = response?.status
      err.data = response?.data
      throw err
    } catch (error) {
      console.error('Error activating eco recommendation:', error)
      throw error
    }
  },

  /**
   * Monthly emission trend (user / objective / FR average) for the line
   * chart. Backend caches at 1h. LAP-752 Phase 4.
   *
   * @param {string} period - "6m" | "12m" | "5y" (default 12m)
   * @returns {Promise<{period: string, series: Array}>}
   */
  getEmissionTrend: async (period = '12m') => {
    try {
      const response = await makeGetRequest(
        `/api/Dashboard/emission-trend/?period=${encodeURIComponent(period)}`,
        {},
        true
      )
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch emission trend')
    } catch (error) {
      console.error('Error fetching emission trend:', error)
      throw error
    }
  },

  /**
   * Regenerate the daily eco recommendation manually (button "Nouvelle
   * recommandation"). Costs 1 credit and synchronously calls Claude.
   * LAP-752 follow-up. Returns the freshest DisplayedAdvice row on success.
   *
   * @returns {Promise<Object>} new advice payload
   * @throws Error with `.status` 402 (insufficient credits) / 500
   */
  regenerateEcoRecommendation: async () => {
    try {
      const response = await makePostRequest(
        '/api/Dashboard/regenerate-eco-recommendation/',
        {},
        {},
        true
      )
      if (response?.status === 200) return response.data
      // LAP-752 v5 — On remonte la cause la plus précise disponible. Un 500 brut
      // (page HTML Django) n'a ni `detail` ni `error` : on l'indique explicitement
      // avec le code HTTP plutôt qu'un message générique opaque.
      const d = response?.data
      const msg =
        (d && (d.detail || d.error)) ||
        (response?.status
          ? `Échec serveur (HTTP ${response.status}) — voir les logs backend`
          : 'Failed to regenerate recommendation')
      const err = new Error(msg)
      err.status = response?.status
      err.data = d
      throw err
    } catch (error) {
      console.error('Error regenerating eco recommendation:', error)
      throw error
    }
  },

  /**
   * Per-category comparison against the French (ADEME) average. Used by
   * the "Vous vs moyenne FR" bar chart. LAP-752 Phase 4.
   *
   * @returns {Promise<{categories: Array, fr_total_t: number}>}
   */
  getEmissionVsFrance: async () => {
    try {
      const response = await makeGetRequest(
        '/api/Dashboard/emission-vs-france/',
        {},
        true
      )
      if (response?.status === 200) return response.data
      throw new Error('Failed to fetch emission vs France')
    } catch (error) {
      console.error('Error fetching emission vs France:', error)
      throw error
    }
  },

  /**
   * Save time breakdown from wizard (Spec 4.12)
   * @param {Array} breakdown - Array of {id, domain, current_hours, desired_hours}
   * @returns {Promise<Object>} Success response
   */
  saveTimeBreakdown: async (breakdown) => {
    try {
      if (!Array.isArray(breakdown)) {
        throw new Error('Breakdown must be an array')
      }
      
      const response = await makePostRequest(
        '/api/app/wizard/time-breakdown/save/',
        { breakdown },
        {},
        true
      )
      
      if (response?.status === 200 || response?.status === 201) {
        return response.data
      }
      
      throw new Error('Failed to save time breakdown')
    } catch (error) {
      console.error('Error saving time breakdown:', error)
      throw error
    }
  }
}
